import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import RadioIcon from '@mui/icons-material/Radio';
import TvIcon from '@mui/icons-material/Tv';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../../api/client';
import type { LiveTvLineupChannel, LiveTvProviderSummary } from '../../api/types';
import { useApi } from '../../api/useApi';
import AsyncSection from '../AsyncSection';
import ChannelFilterBar from './ChannelFilterBar';
import ChannelOverrideDialog from './ChannelOverrideDialog';
import type { ChannelFilter, VisibilityFilter } from './lineup';
import { compareChannels, emptyChannelFilter, matchesFilter, useProviderName } from './lineup';

/// How many rows are drawn before somebody asks for more. A satellite brings two thousand
/// channels, and nobody reads two thousand rows - they search.
const PAGE = 200;

/// Every channel from every plugin, hidden ones included, and the way to change one.
export default function ChannelListPanel({ providers }: { providers: LiveTvProviderSummary[] | undefined }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const providerName = useProviderName(providers);

  const lineup = useApi(['livetv', 'lineup'], api.liveTvLineup);

  const [filter, setFilter] = useState<ChannelFilter>(emptyChannelFilter);
  const [visibility, setVisibility] = useState<VisibilityFilter>('all');
  const [shown, setShown] = useState(PAGE);
  const [editing, setEditing] = useState<LiveTvLineupChannel>();

  const filtered = useMemo(
    () =>
      (lineup.data ?? [])
        .filter(
          (entry) =>
            matchesFilter(entry.channel, filter, [entry.original.name])
            && (visibility === 'all' || (visibility === 'hidden') === entry.isHidden),
        )
        .sort((left, right) => compareChannels(left.channel, right.channel)),
    [lineup.data, filter, visibility],
  );

  const changeFilter = (next: ChannelFilter) => {
    setFilter(next);
    setShown(PAGE);
  };

  return (
    <Stack spacing={2}>
      <ChannelFilterBar filter={filter} onChange={changeFilter} providers={providers}>
        <TextField
          select
          size="small"
          label={t('livetvChannels.filter.visibility')}
          value={visibility}
          onChange={(event) => {
            setVisibility(event.target.value as VisibilityFilter);
            setShown(PAGE);
          }}
          sx={{ flex: '1 1 140px', minWidth: 0 }}
        >
          <MenuItem value="all">{t('livetvChannels.filter.allVisibility')}</MenuItem>
          <MenuItem value="visible">{t('livetvChannels.filter.visible')}</MenuItem>
          <MenuItem value="hidden">{t('livetvChannels.filter.hidden')}</MenuItem>
        </TextField>
      </ChannelFilterBar>

      <AsyncSection loading={lineup.loading} error={lineup.error} onRetry={lineup.reload}>
        {(lineup.data ?? []).length === 0 ? (
          <Alert severity="info">{t('livetvChannels.channels.empty')}</Alert>
        ) : (
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary">
              {t('livetvChannels.channels.count', { shown: filtered.length, total: lineup.data?.length ?? 0 })}
            </Typography>

            <List disablePadding sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
              {filtered.slice(0, shown).map((entry) => {
                const { channel, original } = entry;
                const renamed = channel.name !== original.name;

                return (
                  <ListItem
                    key={`${channel.providerId}\n${channel.id}`}
                    divider
                    secondaryAction={
                      <IconButton
                        edge="end"
                        aria-label={t('livetvChannels.channels.edit', { name: channel.name })}
                        onClick={() => setEditing(entry)}
                      >
                        <EditIcon />
                      </IconButton>
                    }
                    sx={{ opacity: entry.isHidden ? 0.6 : 1, paddingInlineEnd: 7 }}
                  >
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0, flexGrow: 1 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ width: 48, flexShrink: 0, textAlign: 'end', fontVariantNumeric: 'tabular-nums' }}
                      >
                        {channel.number ?? '–'}
                      </Typography>

                      {channel.kind === 'Radio' ? (
                        <RadioIcon fontSize="small" color="action" aria-label={t('livetvChannels.kind.Radio')} />
                      ) : (
                        <TvIcon fontSize="small" color="action" aria-label={t('livetvChannels.kind.Television')} />
                      )}

                      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Typography noWrap sx={{ fontWeight: 600 }}>
                          {channel.name}
                        </Typography>
                        <Stack direction="row" useFlexGap spacing={0.75} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {providerName(channel.providerId)}
                          </Typography>
                          {renamed && (
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {t('livetvChannels.channels.originally', { name: original.name })}
                            </Typography>
                          )}
                          {channel.isEncrypted && (
                            <Tooltip title={t('livetvChannels.channels.encrypted')}>
                              <LockIcon sx={{ fontSize: 14 }} color="action" aria-label={t('livetvChannels.channels.encrypted')} />
                            </Tooltip>
                          )}
                          {entry.isHidden && (
                            <Chip
                              size="small"
                              icon={<VisibilityOffIcon />}
                              label={t('livetvChannels.channels.hidden')}
                            />
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                  </ListItem>
                );
              })}
            </List>

            {filtered.length > shown && (
              <Box>
                <Button onClick={() => setShown(shown + PAGE)}>
                  {t('livetvChannels.channels.more', { total: filtered.length - shown })}
                </Button>
              </Box>
            )}
          </Stack>
        )}
      </AsyncSection>

      {editing && (
        <ChannelOverrideDialog
          entry={editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);

            // A name, a number, a guide or a hidden flag: every list of channels may show it.
            for (const key of ['lineup', 'channels', 'bouquet', 'guide']) {
              void queryClient.invalidateQueries({ queryKey: ['livetv', key] });
            }
          }}
        />
      )}
    </Stack>
  );
}
